/**
 * useDashboard Hook
 * Manages dashboard state and actions
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserAttempts, startTest } from '../services/api';
import { Attempt } from '../types/dashboard';

interface UseDashboardReturn {
  attempts: Attempt[];
  remainingAttempts: number;
  loading: boolean;
  starting: boolean;
  showInstructions: boolean;
  setShowInstructions: (show: boolean) => void;
  handleStartTest: () => Promise<void>;
  handleLogout: () => void;
}

export const useDashboard = (): UseDashboardReturn => {
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [remainingAttempts, setRemainingAttempts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const navigate = useNavigate();

  const userId = localStorage.getItem('userId');

  // Load user attempts
  const loadAttempts = useCallback(async () => {
    if (!userId) return;
    
    try {
      const response = await getUserAttempts(Number(userId));
      setAttempts(response.data.attempts || []);
      setRemainingAttempts(response.data.remaining_attempts || 0);
    } catch (err) {
      console.error('Failed to load attempts:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Check auth and load attempts
  useEffect(() => {
    if (!userId) {
      navigate('/login');
      return;
    }
    loadAttempts();
  }, [userId, loadAttempts, navigate]);

  // Start test handler
  const handleStartTest = useCallback(async () => {
    setStarting(true);
    try {
      const response = await startTest(Number(userId), 'auto');
      localStorage.setItem('sessionId', response.data.session_id);
      localStorage.setItem('attemptId', response.data.attempt_id.toString());
      navigate('/test');
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Echec du demarrage');
      setStarting(false);
    }
  }, [userId, navigate]);

  // Logout handler
  const handleLogout = useCallback(() => {
    localStorage.clear();
    navigate('/login');
  }, [navigate]);

  return {
    attempts,
    remainingAttempts,
    loading,
    starting,
    showInstructions,
    setShowInstructions,
    handleStartTest,
    handleLogout
  };
};
